import clsx from 'clsx';
import { observer } from 'mobx-react';
import * as React from 'react';
import ModalStore from '../../../Modal/Store';
import { LearningCollectionSummaryResource } from '../../Types';
import IndexContext from '../IndexContext';
import Body from './Body';
import Clone from './Clone';
import CloneStore from './Clone/Store';
import CollaboratorAccess from './CollaboratorAccess';
import AccessUpdatedToaster from './CollaboratorAccess/AccessUpdatedToaster';
import CollaboratorAccessStore from './CollaboratorAccess/Store';
import Competencies from './Competencies';
import Header from './Header';
import Hide from './Hide';
import Share from './Share';
import LearningCollectionStore from './Store';

interface Props {
  shareModalStore: ModalStore;
  hideModalStore: ModalStore;
  cloneStore: CloneStore;
  collaboratorAccessStore: CollaboratorAccessStore;
  learningCollectionStore: LearningCollectionStore;
  learningCollectionSummaryResource: LearningCollectionSummaryResource;
  totalLength?: number;
}

function LearningCollectionListItemReady({
  learningCollectionSummaryResource,
  shareModalStore,
  learningCollectionStore,
  hideModalStore,
  cloneStore,
  collaboratorAccessStore,
  totalLength,
}: Props) {
  const { id, name, publishedAt, shareUrl } = learningCollectionSummaryResource.payload;
  const index = React.useContext(IndexContext);
  const cardRef = React.useRef<HTMLDivElement>(null);
  const listStore = learningCollectionStore.learningCollectionsStore;
  const isRecentlyCloned = listStore.recentlyClonedCollectionId
    .map((clonedId) => clonedId === id)
    .getOrElseValue(false);
  const isAccessRecentlyUpdated = listStore.recentlyUpdatedAccessCollectionId
    .map((updatedId) => updatedId === id)
    .getOrElseValue(false);

  React.useEffect(() => {
    if (isRecentlyCloned && cardRef.current) {
      cardRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const timer = window.setTimeout(() => listStore.clearRecentlyCloned(), 3000);
      return () => window.clearTimeout(timer);
    }
    return undefined;
  }, [isRecentlyCloned, listStore]);

  // The toaster is transient, like the clone highlight; the store flag is
  // list-wide so it survives the refetch that follows a save.
  React.useEffect(() => {
    if (isAccessRecentlyUpdated) {
      const timer = window.setTimeout(() => listStore.clearRecentlyUpdatedAccess(), 5000);
      return () => window.clearTimeout(timer);
    }
    return undefined;
  }, [isAccessRecentlyUpdated, listStore]);

  return (
    <>
      <div
        ref={cardRef}
        className={clsx(
          `mb-2 rounded-md bg-white px-2.5 py-6 drop-shadow-md last:mb-0 lg:px-8 relative`,
          'transition-shadow duration-700',
          isRecentlyCloned && 'ring-2 ring-primary',
        )}
        style={{ zIndex: index }}
      >
        {isAccessRecentlyUpdated && <AccessUpdatedToaster />}
        <Header
          learningCollectionSummaryResource={learningCollectionSummaryResource}
          shareModalStore={shareModalStore}
          hideModalStore={hideModalStore}
          cloneStore={cloneStore}
          collaboratorAccessStore={collaboratorAccessStore}
          learningCollectionStore={learningCollectionStore}
          index={index}
          totalLength={totalLength}
        />
        <div className="flex">
          <Body learningCollectionSummaryResource={learningCollectionSummaryResource} />
          <div className="mt-6 basis-1/2 pl-2.5">
            <Competencies learningCollectionSummaryResource={learningCollectionSummaryResource} />
          </div>
        </div>
      </div>
      <Share
        name={name}
        shareModalStore={shareModalStore}
        publishedAt={publishedAt}
        shareUrlResource={shareUrl}
        learningCollectionStore={learningCollectionStore}
      />
      <Hide
        name={name}
        learningCollectionStore={learningCollectionStore}
        hideModalStore={hideModalStore}
      />
      <Clone
        cloneStore={cloneStore}
        learningCollectionsStore={learningCollectionStore.learningCollectionsStore}
      />
      <CollaboratorAccess
        store={collaboratorAccessStore}
        collectionId={id}
        collectionName={name}
        learningCollectionsStore={listStore}
      />
    </>
  );
}

export default observer(LearningCollectionListItemReady);
